import { Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import TableBasedCrud, { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import OpsDateUtils from "../utils/OpsDateUtils";
import AccomplishmentService from "../services/AccomplishmentService";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import { ROWS_PER_PAGE } from "../App";
import { Accomplishment } from "../models/Accomplishment";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import AccomplishmentCreateModal from "../components/Accomplishment/AccomplishmentCreateModal";
import AccomplishmentUtils from "../utils/AccomplishmentUtils";
import { AccomplishmentStatusFilters } from "../utils/enums/filters/AccomplishmentStatusFilters";
import AccomplishmentEditFormModal from "../components/Accomplishment/AccomplishmentEditFormModal";
import AccomplishmentDetailsDialog from "../components/Accomplishment/AccomplishmentDetailsDialog";
import Assignation from "../models/Assignation";
import Position from "../models/Position";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import useTableHeight from "../hooks/useTableHeight";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useStyles } from "../components/Main/formStyles";
import { ColorUtils } from "../utils/ColorUtils";
import { Box } from "@mui/material";

export default function AccomplishmentSection() {
  const [selectedAccomplishment, setSelectedAccomplishment] = useState<Accomplishment>();
  const [update, setUpdate] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [links, setLinks] = useState<string[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentAccomplishment, setCurrentAccomplishment] = useState<Accomplishment | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [accomplishment, setAccomplishment] = useState<Accomplishment | null>();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const [totalPoints, setTotalPoints] = useState<number>(0);

  const customStyles = useStyles();

  useEffect(() => {
    getMonthlyTotalPoints();
  }, [update]);

  const getMonthlyTotalPoints = async () => {
    let points: number = 0;
    let response = await AccomplishmentService.getMonthlyTotalPoints();
    points = response.getContentOrThrowError();
    setTotalPoints(points || 0);
  };

  const handleOnDeleteAccomplishment = () => {
    if (!selectedAccomplishment) return;
    console.log(selectedAccomplishment);
    AccomplishmentService.deleteAccomplishment(selectedAccomplishment.externalId!).then(() => {
      setSelectedAccomplishment(undefined);
      setUpdate(Date.now());
      NotificationService.info("Accomplishment deleted successfully");
    });
  };

  const getInfoProvided = (accomplishment: Accomplishment): string => {
    let linkCount: number = 0;
    if (accomplishment.info) linkCount = accomplishment.info.length;
    if (linkCount < 1) {
      return "(none)";
    }
    if (linkCount === 1) {
      return "1 entry";
    }
    return `${linkCount} entries`;
  };

  const handleViewDetails = (instance: Accomplishment) => {
    setAccomplishment(instance);
    setIsOpen(true);
  };

  const getStatusMessage = (accomplishment: Accomplishment): JSX.Element => {
    if (accomplishment.status === "Approved" && accomplishment.approvedDate) {
      let approveDate = OpsDateUtils.ensureFormatDateUTC(accomplishment.approvedDate);
      return (
        <>
          <span>{accomplishment.status}</span>
          <br></br>
          <span>{approveDate}</span>
        </>
      );
    }
    return <span>{accomplishment.status}</span>;
  };

  const getAditionalActions = (instance: Accomplishment, refresh: () => void): ActionsMenuEntry[] => {
    return [
      {
        title: "View Details",
        action: () => handleViewDetails(instance),
      },
      ...(isPending(instance)
        ? [
            {
              title: "Edit",
              action: () => {
                setLinks(instance.info || []);
                if (instance.assignation?.externalId === undefined) {
                  const emptyAssignation: Assignation = {
                    userFullName: "",
                    shortStartDate: new Date(),
                    shortEndDate: undefined,
                    position: {} as Position,
                    userExternalId: "",
                  };
                  emptyAssignation.externalId = "No role";
                  instance.assignation = emptyAssignation;
                }
                setCurrentAccomplishment(instance);
                setIsEdit(true);
              },
            },
            {
              title: "Delete",
              action: () => setSelectedAccomplishment(instance),
            },
          ]
        : []),
    ];
  };

  const handleOnDataFetch = (pageNumber: number, count: number): Promise<PagedResult<Accomplishment>> => {
    return AccomplishmentService.getMyAccomplishments(pageNumber, count).then((result) => result.getContentOrThrowError());
  };

  const handleOnCreate = (instance: Accomplishment) => {
    instance.info = links!;
    AccomplishmentService.createAccomplishment(AuthService.getUser()?.externalId!, instance)
      .then(() => {
        NotificationService.info("Accomplishment created successfully");
        setUpdate(Date.now());
      })
      .catch((err) => NotificationService.error(err.response.data.message));
    setLinks([]);
  };

  const handleOnUpdate = (instance: Accomplishment) => {
    instance.info = links || [];
    AccomplishmentService.updateAccomplishment(instance.externalId!, instance)
      .then(() => {
        NotificationService.info("Accomplishment updated successfully");
        setUpdate(Date.now());
      })
      .catch((err) => NotificationService.error(err.response.data.message));
    setLinks([]);
    setIsEdit(false);
    setCurrentAccomplishment(undefined);
  };

  const isPending = (instance: Accomplishment) => {
    return instance.status === AccomplishmentStatusFilters.PENDING;
  };

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <AppToolbar title={"My accomplishments"}>
          <Typography>{`Total points this month: ${totalPoints}`}</Typography>
        </AppToolbar>
      }
      bottomSection={
        <Box sx={{boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px"}}>
          <TableBasedCrud<Accomplishment>
            headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
            buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
            cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
            dialogStyles={ customStyles.root }
            buttonContainer={ customButtonsContainer }
            gridButtonContainer={ gridButtonContainer }
            addButtonBuilder={ customButtonBuilder }
            maxHeight={calculateMaxHeight(true)}
            stickyHeader
            stickyPagination
            elementName={"My accomplishments"}
            rowsPerPage={ROWS_PER_PAGE}
            onDataFetch={handleOnDataFetch}
            onCreate={handleOnCreate}
            onCancelDialog={() => setLinks([])}
            forceUpdateOn={update}
            denseTable
            disableAutoFetch={true}
            resetValue={{ type: "", assignationExternalId: "", positionExternalId: "", info: [] }}
            tableColumns={[
              {
                header: "Type",
                content: (instance) => <>{instance.type.title}</>,
              },
              {
                header: "Performed On",
                content: (instance) => <>{OpsDateUtils.ensureFormatDateUTC(instance.reportedOn)}</>,
              },
              {
                header: "Entries",
                content: (instance) => <>{getInfoProvided(instance)}</>,
              },
              {
                header: "Status",
                content: (instance) => (
                  <label
                    style={{
                      color: AccomplishmentUtils.getStatusColor(instance.status),
                    }}
                  >
                    {instance.status.replace("_", " ")}
                  </label>
                ),
              },
              {
                header: "Approved by",
                content: (instance) => <>{instance.approver ? `${instance.approver.firstName} ${instance.approver.lastName}` : ""}</>,
              },
              {
                header: "Approved on",
                content: (instance) => <>{OpsDateUtils.ensureFormatDateUTC(instance.approvedDate)}</>,
              },
              {
                header: "Points earned",
                content: (instance) => <>{instance.points}</>,
              },
              {
                header: "Role",
                content: (instance) => <>{instance.role ? instance.role : "No role"}</>,
              },
            ]}
            buildAdditionalActions={getAditionalActions}
            renderDialogContent={(register, errors, control, instance) => (
              <AccomplishmentCreateModal
                register={register}
                errors={errors}
                control={control}
                isLoading={isLoading}
                setLinks={setLinks}
                links={links}
                instance={instance!}
              />
            )}
          />
          <AppDialog
            open={!!selectedAccomplishment}
            onClose={() => setSelectedAccomplishment(undefined)}
            title={"Delete"}
            content={
              <Typography>
                {`The accomplishment you selected is going to be eliminated. 
            Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteAccomplishment() }]}
          />

          <AccomplishmentEditFormModal
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            currentAccomplishment={currentAccomplishment}
            setCurrentAccomplishment={setCurrentAccomplishment}
            handleOnUpdate={handleOnUpdate}
            links={links}
            setLinks={setLinks}
            isLoading={isLoading}
          />

          <AccomplishmentDetailsDialog isOpen={isOpen} setIsOpen={setIsOpen} accomplishment={accomplishment!} hideApproveButton={true}/>
        </Box>
      }
    />
  );
}
